<template>
  <mf-loading-dialog :loading="isLoading">
    <v-container fluid>
      <raffle-participants-dialog v-if="showParticipantsDialog" @close="closeParticipantsDialog" />
      <add-lucky-number-dialog v-if="showAddLuckyNumberDialog" @close="closeAddLuckyNumberDialog" @lucky-number-added="$emit('refreshPage')" />
      <register-winner-dialog
        v-if="showRegisterWinnerDialog"
        :prize="editingPrize"
        :raffle="raffle"
        :savePrize="savePrize"
        @close="closeRegisterWinnerDialog"
      />
      <v-row>
        <v-col cols="12" class="pa-0">
          <v-row>
            <v-col cols="6" class="pa-2 pl-3">
              <v-card height="200px" class="d-flex flex-column">
                <v-card-title>Números da sorte gerados até agora</v-card-title>
                <h1 class="pl-4">{{ luckyNumbersMetrics.total_lucky_numbers | localeString }}</h1>
                <div class="d-flex align-end px-4 pb-3 justify-space-between flex-1">
                  <v-tooltip :disabled="hasPiiAccess" max-width="250" top content-class="truncated-tooltip">
                    <template v-slot:activator="{ on }">
                      <span v-on="on">
                        <mf-button
                          text
                          color="marca-0-l40"
                          icon="add"
                          label="Adicionar número da sorte"
                          :disabled="!hasPiiAccess"
                          @click="openAddLuckyNumberDialog"
                        />
                      </span>
                    </template>
                    <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
                  </v-tooltip>
                </div>
              </v-card>
            </v-col>

            <v-col cols="6" class="pa-2 pr-3">
              <v-card height="200px" class="d-flex flex-column">
                <v-card-title>Primeiro e o último número gerado</v-card-title>
                <h1 class="pl-4">
                  {{
                    `${this.$options.filters.localeString(luckyNumbersMetrics.first_lucky_number | localeString)} - ${this.$options.filters.localeString(
                      luckyNumbersMetrics.last_lucky_number | localeString
                    )}`
                  }}
                </h1>
                <h3 class="pl-4">Números da sorte válidos para o próximo sorteio</h3>
                <div class="d-flex align-end px-4 pb-3 justify-space-between flex-1">
                  <div class="d-flex align-center">
                    <v-tooltip :disabled="hasPiiAccess" max-width="250" top content-class="truncated-tooltip">
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <mf-button
                            label="Ver lista de participantes"
                            icon="format_list_bulleted"
                            text
                            color="marca-0-l40"
                            :disabled="!hasPiiAccess"
                            @click="openParticipantsDialog"
                          />
                        </span>
                      </template>
                      <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
                    </v-tooltip>
                  </div>

                  <div class="d-flex align-center">
                    <v-tooltip :disabled="hasPiiAccess" max-width="250" top content-class="truncated-tooltip">
                      <template v-slot:activator="{ on }">
                        <span v-on="on">
                          <export-raffle-participants-dialog :raffle-id="$route.params.id" :disabled="!hasPiiAccess" />
                        </span>
                      </template>
                      <span>Você precisa ter a permissão de acesso a informações privilegiadas para acessar esse recurso.</span>
                    </v-tooltip>
                  </div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <prizes-winners-table
        :linksClassList="linksClassList"
        :hasPiiAccess="hasPiiAccess"
        @register_winner="setEditingPrize"
        @generate-winner="handleGenerateWinner"
      />
      <generate-winner-dialog
        ref="generateWinnerDialog"
        v-model="showGenerateWinnerPrizeDialog"
        :data="generateWinnerPrizeDialogData"
        @close="closeGenerateWinnerPrizeDialog"
      />
    </v-container>
  </mf-loading-dialog>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import PrizesWinnersTable from './PrizesWinnersTable.vue'
import RegisterWinnerDialog from './RegisterWinnerDialog.vue'
import RaffleParticipantsDialog from './RaffleParticipantsDialog.vue'
import ExportRaffleParticipantsDialog from './ExportRaffleParticipantsDialog.vue'
import AddLuckyNumberDialog from './AddLuckyNumberDialog.vue'
import GenerateWinnerDialog from './GenerateWinnerDialog.vue'
import { MUTATION_UPSERT_RAFFLE_WINNERS } from '../../graphql'

export default {
  name: 'EditRaffleWinners',
  components: {
    PrizesWinnersTable,
    RegisterWinnerDialog,
    RaffleParticipantsDialog,
    ExportRaffleParticipantsDialog,
    AddLuckyNumberDialog,
    GenerateWinnerDialog,
  },
  props: {
    isLoading: {
      type: Boolean,
      required: true,
    },
  },
  data: () => ({
    prizeSearch: '',
    editingPrize: null,
    generateWinnerPrizeDialogData: { title: '', raffle_date: '', quantity: '', winners: [] },
    showGenerateWinnerPrizeDialog: false,
    showRegisterWinnerDialog: false,
    showParticipantsDialog: false,
    showAddLuckyNumberDialog: false,
  }),
  computed: {
    ...mapState({
      raffle: (state) => state.sorteio.raffle,
      luckyNumbersMetrics: (state) => state.sorteio.luckyNumbersMetrics,
    }),
    ...mapGetters(['generateWinner']),
    getIconColor() {
      return this.hasPiiAccess ? '#0079f2' : '#a5acb4'
    },
    hasPiiAccess() {
      return this.$hasPiiAccess()
    },
    linksClassList() {
      const classList = {
        'pl-4': true,
        'mr-2': true,
        'blue-action-text': this.hasPiiAccess,
        'gray-disabled-text': !this.hasPiiAccess,
      }
      return classList
    },
  },
  mounted() {
    const { selectedPrize } = this.generateWinner
    if (this.$route.query?.fromRaffleResult && selectedPrize) {
      this.generateWinnerPrizeDialogData = selectedPrize
      this.showGenerateWinnerPrizeDialog = true
      this.$route.query.fromRaffleResult = false
      this.$refs.generateWinnerDialog.generateWinner = {
        ...this.generateWinner,
      }
    }
  },
  methods: {
    closeGenerateWinnerPrizeDialog() {
      this.showGenerateWinnerPrizeDialog = false
      this.generateWinnerPrizeDialogData = { title: '', raffle_date: '', quantity: '', winners: [] }
    },
    setEditingPrize(prize) {
      if (this.hasPiiAccess) {
        this.editingPrize = prize
        this.openRegisterWinnerDialog()
      }
    },
    handleGenerateWinner(prize) {
      this.generateWinnerPrizeDialogData = prize
      this.showGenerateWinnerPrizeDialog = true
    },
    openRegisterWinnerDialog() {
      this.showRegisterWinnerDialog = true
    },
    closeRegisterWinnerDialog() {
      this.showRegisterWinnerDialog = false
    },
    openAddLuckyNumberDialog() {
      if (this.hasPiiAccess) {
        this.showAddLuckyNumberDialog = true
      }
    },
    openParticipantsDialog() {
      if (this.hasPiiAccess) {
        this.showParticipantsDialog = true
      }
    },
    closeParticipantsDialog() {
      this.showParticipantsDialog = false
    },
    closeAddLuckyNumberDialog() {
      this.showAddLuckyNumberDialog = false
    },
    async savePrize(formattedWinners, prizeId) {
      this.$apollo
        .mutate({
          mutation: MUTATION_UPSERT_RAFFLE_WINNERS,
          variables: {
            raffleId: this.$route.params.id,
            prizeId: prizeId,
            winners: formattedWinners,
          },
          fetchPolicy: 'no-cache',
        })
        .then((response) => {
          if (response.data?.upsertWinners) {
            this.$snackbar({ message: 'Sucesso ao editar prêmio', snackbarColor: 'success' })
            this.closeRegisterWinnerDialog()
            this.$emit('refreshPage')
          }
        })
        .catch((err) => {
          console.log(err)
          this.$snackbar({ message: 'Erro ao salvar prêmio', snackbarColor: 'error' })
        })
    },
  },
}
</script>

<style lang="scss" scoped>
.mf-subtitle-2 {
  font-family: 'Lato', sans-serif;
  font-weight: 400;
  font-size: 16px;
  letter-spacing: 0.2px;
  padding-bottom: 5px;
}
h1 {
  color: $cinza-0;
  font-family: Lato;
  font-size: 36px;
  font-style: normal;
  font-weight: 700;
  line-height: 43px;
  letter-spacing: 0.01em;
  text-align: left;
}
h3 {
  font-family: Lato;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  letter-spacing: 0.01em;
  text-align: left;
  color: $cinza-0;
}
.blue-action-text {
  color: $marca-0-l40;
  text-decoration: underline;
  text-align: left;
  &:hover {
    cursor: pointer;
  }
}
.gray-disabled-text {
  font-family: Lato;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.216667px;
  text-decoration-line: underline;
  color: $cinza-l40;
}
.hint-text {
  font-size: 14px;
  line-height: 21px;
  &.external {
    max-width: 700px;
  }
  b {
    color: $marca-1;
    font-weight: 700;
  }
}
.raffle-info {
  min-height: 600px;
}
.raffle-title {
  word-break: normal;
}

.flex-1 {
  flex: 1;
}
</style>
